<div class="space-y-6">
  <!-- ── Activation ── -->
  <section hlmCard>
    <div hlmCardHeader>
      <h2 hlmCardTitle>Netzwerk aktivieren</h2>
      <p hlmCardDescription>
        Aktiviere den Netzwerk-Modus, um eine Werbegemeinschaft oder ein Franchise zu betreiben.
        Dein Account wird zur Zentrale des Netzwerks: du verkaufst Gutscheine, deine Mitglieder
        lösen sie ein, und du überweist ihnen die Einlösungen.
      </p>
      @if (company?.is_group_admin) {
        <div hlmCardAction>
          <span hlmBadge variant="solid">Aktiv</span>
        </div>
      }
    </div>

    <div hlmCardContent class="space-y-3">
      @if (company?.is_member) {
        <div hlmAlert>
          <ng-icon name="lucideInfo" />
          <p hlmAlertTitle>Dein Account ist Mitglied eines Netzwerks</p>
          <p hlmAlertDescription>
            @if (company?.member_of_company_name) {
              Zentrale: {{ company?.member_of_company_name }}.
            }
            Der Netzwerk-Modus kann nicht gleichzeitig aktiviert werden.
          </p>
        </div>
      } @else if (!company?.is_group_admin) {
        <button hlmBtn [disabled]="activating" (click)="toggleGroupEnabled(true)">
          @if (activating) {
            <hlm-spinner />
          } @else {
            <ng-icon name="lucideUsers" />
          }
          Netzwerk aktivieren
        </button>
        @if (company?.company_covers_customer_fee) {
          <p hlmMuted class="text-xs">
            Im Netzwerk zahlt immer die Kundschaft die Servicegebühr von 0,99 €. Mit der Aktivierung
            übernimmst du sie nicht mehr.
          </p>
        }
      } @else if (company?.group_locked_at) {
        <p hlmMuted class="text-xs">
          Das Netzwerk kann nicht mehr deaktiviert werden, weil es bereits Mitglieder hatte oder
          Einladungen verschickt wurden. Bei Fragen wende dich an den
          <a href="mailto:support@schenkwerk.de" class="underline underline-offset-4">
            Kundensupport </a
          >.
        </p>
      } @else {
        <div class="space-y-2">
          <button
            hlmBtn
            variant="outline"
            size="sm"
            [disabled]="activating"
            (click)="toggleGroupEnabled(false)">
            Netzwerk deaktivieren
          </button>
          <p hlmMuted class="text-xs">
            Sobald du Mitglieder einlädst oder jemand beitritt, bleibt das Netzwerk dauerhaft aktiv.
          </p>
        </div>
      }

      @if (activateSuccess) {
        <p hlmMuted class="text-sm">Gespeichert.</p>
      }
      @if (activateError) {
        <p class="text-destructive text-sm">{{ activateError }}</p>
      }
    </div>
  </section>

  <!-- ── Configuration ── -->
  @if (company?.is_group_admin) {
    <section hlmCard>
      <div hlmCardHeader>
        <h2 hlmCardTitle>Netzwerk-Einstellungen</h2>
        <p hlmCardDescription>
          Konfiguriere, welche Informationen und Funktionen deinen Mitgliedern zur Verfügung stehen.
        </p>
      </div>

      <div hlmCardContent class="space-y-3">
        <div hlmItem variant="outline">
          <div hlmItemContent>
            <span hlmItemTitle>Netzwerk-Statistik für Mitglieder</span>
            <span hlmItemDescription>
              Wenn aktiviert, können Mitglieder die gesamte Statistik des Netzwerks (Umsatz,
              Gutscheinliste) im Dashboard einsehen.
            </span>
          </div>
          <div hlmItemActions>
            <hlm-switch
              aria-label="Netzwerk-Statistik für Mitglieder"
              [checked]="!!company?.members_can_see_reporting"
              [disabled]="savingReporting"
              (checkedChange)="toggleMembersCanSeeReporting($event)" />
          </div>
        </div>

        <a
          hlmItem
          variant="outline"
          [routerLink]="['/settings']"
          [queryParams]="{ tab: 'verbund-members' }">
          <div hlmItemMedia variant="icon"><ng-icon name="lucideUsers" /></div>
          <div hlmItemContent>
            <span hlmItemTitle>Mitglieder verwalten</span>
            <span hlmItemDescription>Einladungen, Beitritte und Auszahlungen</span>
          </div>
          <div hlmItemActions>
            <ng-icon name="lucideChevronRight" class="text-muted-foreground" />
          </div>
        </a>
      </div>
    </section>
  }

  <!-- ── How it works ── -->
  <section hlmCard>
    <div hlmCardHeader>
      <h2 hlmCardTitle>So funktioniert das Netzwerk</h2>
      <p hlmCardDescription>Rollen, Geldfluss und Einlösemethoden im Überblick</p>
    </div>

    <div hlmCardContent class="space-y-6">
      <!-- Roles -->
      <div class="grid gap-3 sm:grid-cols-2">
        <div hlmItem variant="outline" class="flex-col items-start gap-2">
          <div>
            <p class="text-sm font-medium">Zentrale</p>
            <p hlmMuted class="text-xs">Organisiert das Netzwerk</p>
          </div>
          <ul class="space-y-1.5">
            @for (duty of centralDuties; track duty) {
              <li class="text-muted-foreground flex items-start gap-1.5 text-xs">
                <ng-icon name="lucideCheck" class="mt-0.5 shrink-0" />
                {{ duty }}
              </li>
            }
          </ul>
        </div>

        <div hlmItem variant="outline" class="flex-col items-start gap-2">
          <div>
            <p class="text-sm font-medium">Mitglied</p>
            <p hlmMuted class="text-xs">Geschäft im Netzwerk</p>
          </div>
          <ul class="space-y-1.5">
            @for (duty of memberDuties; track duty) {
              <li class="text-muted-foreground flex items-start gap-1.5 text-xs">
                <ng-icon name="lucideCheck" class="mt-0.5 shrink-0" />
                {{ duty }}
              </li>
            }
          </ul>
        </div>
      </div>

      <!-- Money flow -->
      <div class="space-y-2">
        <h3 hlmSmall>Geldfluss</h3>
        <div class="grid gap-2 sm:grid-cols-4">
          @for (step of moneyFlow; track step.title) {
            <div hlmItem variant="muted" class="flex-col items-start gap-0.5">
              <span class="text-xs font-medium">{{ step.title }}</span>
              <span hlmMuted class="text-xs">{{ step.detail }}</span>
            </div>
          }
        </div>
        <p hlmMuted class="text-xs">
          Wie viel ein Mitglied je Einlösung erhält, siehst du unter
          <a
            [routerLink]="['/settings']"
            [queryParams]="{ tab: 'fees-redemption' }"
            class="underline underline-offset-4"
            >Gebühren</a
          >.
        </p>
      </div>

      <!-- Redemption methods -->
      <div class="space-y-2">
        <h3 hlmSmall>Einlösemethoden</h3>
        <div class="grid gap-3 sm:grid-cols-3">
          @for (method of redemptionMethods; track method.title) {
            <div hlmItem variant="outline" class="flex-col items-start gap-0.5">
              <span class="text-xs font-medium">{{ method.title }}</span>
              <span hlmMuted class="text-xs">{{ method.detail }}</span>
            </div>
          }
        </div>
      </div>
    </div>
  </section>
</div>
